/**
 * DataBest — Task-position header for the caregiver scoring flow.
 *
 * The task number is now a dropdown picker: click it to see every task
 * in the current flow with its title, individual and life-area. The
 * search input inside the panel filters by number OR text (title /
 * individual name / life-area), and Enter jumps to the first match.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TONE_STYLES = {
    review: {
        eyebrow: "text-amber-700",
        bar: "bg-amber-500",
        currentBg: "bg-amber-50",
        currentBullet: "bg-amber-500",
        ring: "focus-visible:ring-amber-500/40",
    },
    backlog: {
        eyebrow: "text-red-700",
        bar: "bg-red-600",
        currentBg: "bg-red-50",
        currentBullet: "bg-red-600",
        ring: "focus-visible:ring-red-500/40",
    },
    pending: {
        eyebrow: "text-brand-blue-700",
        bar: "bg-brand-blue-700",
        currentBg: "bg-brand-blue-50",
        currentBullet: "bg-brand-blue-700",
        ring: "focus-visible:ring-brand-blue-500/40",
    },
};

const buildItemMeta = (items = [], individualsById = {}) =>
    items.map((it, idx) => {
        const ind = individualsById?.[it.individual_id];
        const indName = ind
            ? `${ind.first_name} ${ind.last_name}`.trim()
            : (it.individual_id || "").slice(0, 8);
        const lifeArea = it.outcome?.life_area || "";
        const title = it.task?.title || "";
        const searchBlob =
            `${idx + 1} ${title} ${indName} ${lifeArea}`.toLowerCase();
        return { idx, title, indName, lifeArea, searchBlob };
    });

const filterItems = (meta, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return meta;
    const tokens = q.split(/\s+/).filter(Boolean);
    return meta.filter((m) => tokens.every((tok) => m.searchBlob.includes(tok)));
};

const FlowHeader = ({
    label,
    index,
    total,
    tone,
    onJump,
    onBack,
    backLabel = "Back to Today",
    items = [],
    individualsById = {},
}) => {
    const t = TONE_STYLES[tone] || TONE_STYLES.pending;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const listRef = useRef(null);
    const searchInputRef = useRef(null);
    const currentRef = useRef(null);

    const meta = useMemo(
        () => buildItemMeta(items, individualsById),
        [items, individualsById],
    );
    const filtered = useMemo(() => filterItems(meta, query), [meta, query]);

    // Reset the search box each time the dropdown opens, autofocus and
    // scroll the current task into view.
    useEffect(() => {
        if (!open) return;
        setQuery("");
        // Focus + scroll after Radix mounts the content.
        const timer = setTimeout(() => {
            searchInputRef.current?.focus();
            currentRef.current?.scrollIntoView({
                block: "center",
                behavior: "auto",
            });
        }, 30);
        return () => clearTimeout(timer);
    }, [open]);

    const goTo = (newIdx) => {
        onJump(newIdx);
        setOpen(false);
    };

    const onSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (filtered.length > 0) goTo(filtered[0].idx);
        } else if (e.key === "Escape") {
            e.preventDefault();
            if (query) setQuery("");
            else setOpen(false);
        }
    };

    return (
        <>
            <div className="mb-3 flex items-center justify-between gap-2">
                <div
                    className={`flex min-w-0 items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] ${t.eyebrow}`}
                    data-testid="flow-header"
                >
                    <span className="shrink-0">{label}</span>
                    <DropdownMenu open={open} onOpenChange={setOpen}>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                data-testid="task-picker-trigger"
                                title="Click to jump to a specific task"
                                className={`inline-flex min-h-[36px] items-center gap-1 rounded-md px-2 py-1 text-sm font-black tracking-normal text-slate-900 transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 ${t.ring}`}
                                disabled={total <= 0}
                            >
                                <span className="tabular-nums">{index + 1}</span>
                                <span className="text-slate-400">/</span>
                                <span className="tabular-nums text-slate-500">
                                    {total}
                                </span>
                                <ChevronDown className="ml-1 h-3.5 w-3.5 text-slate-500" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="start"
                            sideOffset={6}
                            className="w-[min(92vw,440px)] p-0"
                            onCloseAutoFocus={(e) => e.preventDefault()}
                        >
                            {/* Search */}
                            <div className="sticky top-0 z-10 border-b border-slate-100 bg-white/95 p-2 backdrop-blur">
                                <div className="relative">
                                    <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <input
                                        ref={searchInputRef}
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        onKeyDown={onSearchKeyDown}
                                        type="text"
                                        inputMode="search"
                                        autoComplete="off"
                                        spellCheck={false}
                                        data-testid="task-picker-search"
                                        placeholder="Search number, task or individual…"
                                        className={`h-9 w-full rounded-md border border-slate-200 bg-white pl-8 pr-8 text-base sm:text-sm text-slate-900 focus:outline-none focus:ring-2 ${t.ring}`}
                                    />
                                    {query && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setQuery("");
                                                searchInputRef.current?.focus();
                                            }}
                                            className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                            aria-label="Clear search"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* List */}
                            <div
                                ref={listRef}
                                className="max-h-[65vh] overflow-y-auto py-1"
                                role="listbox"
                            >
                                {filtered.length === 0 ? (
                                    <div
                                        data-testid="task-picker-empty"
                                        className="flex flex-col items-center gap-2 px-3 py-8 text-center text-sm text-slate-500"
                                    >
                                        <p>No tasks match your search.</p>
                                        <button
                                            type="button"
                                            onClick={() => setQuery("")}
                                            className="text-xs font-semibold text-brand-blue-700 hover:underline"
                                        >
                                            Clear search
                                        </button>
                                    </div>
                                ) : (
                                    filtered.map((m) => {
                                        const isCurrent = m.idx === index;
                                        return (
                                            <button
                                                key={m.idx}
                                                type="button"
                                                ref={isCurrent ? currentRef : undefined}
                                                data-current={isCurrent || undefined}
                                                data-testid={
                                                    isCurrent
                                                        ? "task-picker-current"
                                                        : `task-picker-item-${m.idx}`
                                                }
                                                onClick={() => goTo(m.idx)}
                                                className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-slate-50 focus:bg-slate-50 focus:outline-none ${
                                                    isCurrent ? t.currentBg : ""
                                                }`}
                                            >
                                                <span
                                                    aria-hidden="true"
                                                    className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
                                                        isCurrent
                                                            ? t.currentBullet
                                                            : "bg-transparent"
                                                    }`}
                                                />
                                                <span className="w-8 shrink-0 pt-0.5 text-right text-xs font-bold tabular-nums text-slate-400">
                                                    {m.idx + 1}
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block break-words text-sm font-semibold text-slate-900 line-clamp-2 normal-case tracking-normal">
                                                        {m.title || "(untitled)"}
                                                    </span>
                                                    <span className="mt-0.5 block truncate text-xs font-normal text-slate-500 normal-case tracking-normal">
                                                        {m.indName}
                                                        {m.lifeArea
                                                            ? ` · ${m.lifeArea}`
                                                            : ""}
                                                    </span>
                                                </span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>

                            {/* Footer counter (only when filtering) */}
                            {query && (
                                <div
                                    data-testid="task-picker-count"
                                    className="border-t border-slate-100 px-3 py-1.5 text-[11px] normal-case tracking-normal text-slate-500"
                                >
                                    {total} tasks · showing {filtered.length}{" "}
                                    match{filtered.length === 1 ? "" : "es"}
                                </div>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                <button
                    onClick={onBack}
                    data-testid="back-to-today-button"
                    className="shrink-0 text-xs font-semibold text-slate-500 hover:text-brand-blue-700"
                >
                    {backLabel}
                </button>
            </div>
            <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                    className={`h-full transition-all ${t.bar}`}
                    style={{ width: `${((index + 1) / Math.max(total, 1)) * 100}%` }}
                    data-testid="flow-progress-bar"
                />
            </div>
        </>
    );
};

export default FlowHeader;
